import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import SearchBox from 'components/site/SearchBox';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import { Loader2 } from 'lucide-react';
import _ from 'underscore';
import { withTranslation } from 'react-i18next';
import { debounce } from 'lodash';

function onSelect(value, item, history) {
  if (item && item._id) {
    history.push(`/shipments/${item._id}`, { id: item._id });
  }
}

class ShipmentSearch extends Component {
  static propTypes = {
    onSelect: PropTypes.func,
    closeOnSelect: PropTypes.bool,
    onClose: PropTypes.func,
    menuStyle: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      items: [],
    };

    this.search = debounce(this.search, 500);
  }

  clear() {
    this.setState({ items: [] });
  }

  onChange(event) {
    if (this.props.isFilter) {
      this.props.onChange(event);
    }
  }

  search(query, searchTerm) {
    const { invoice, payout, isInvoice, includeAccountingInformation } =
      this.props;
    const element = isInvoice ? invoice : payout;

    query.page = 1;
    query.per_page = 5;
    query.paginate = false;
    query.select =
      'reference_numbers timeline status shipment_id shipment_type';
    query.unconstrain = true;
    query.includeAccountingInformation = !!includeAccountingInformation;

    if (element && element.type) {
      query.shipment_type = element.type;
    }

    if (element && element.shipper) {
      query.shipper =
        element.shipper && element.shipper._id
          ? element.shipper._id
          : element.shipper;
    }

    if (element && element.carrier) {
      query.carrier =
        element.carrier && element.carrier._id
          ? element.carrier._id
          : element.carrier;
    }

    ShipmentWebAPIUtils.getShipmentsByReference(
      query,
      (res) => {
        // Only update if this response matches the current search term
        if (this.state.searchItem === searchTerm) {
          // Filter to only items that actually match the search term
          const items = (res.entity.items || []).filter((item) =>
            item.reference_numbers?.some(
              (ref) => ref.optimized && ref.optimized.indexOf(searchTerm) > -1,
            ),
          );
          this.setState({ items, isLoading: false });
        }
      },
      (res) => {
        console.log('Error', res.body);
      },
    );
  }

  handleSearch(input) {
    const item = input.trim().toUpperCase();
    const { isFilter, lineItemGroups } = this.props;

    if (isFilter) {
      this.setState({
        searchItem: item,
      });
      this.setState({
        items: lineItemGroups,
      });
    } else if (!_.isEmpty(item?.trim())) {
      // Clear items immediately to prevent stale results flashing
      this.setState(
        {
          searchItem: item,
          items: [],
          isLoading: true,
        },
        () => {
          this.search(
            {
              reference: [item],
            },
            item,
          );
        },
      );
    } else {
      this.setState({ items: [], isLoading: false });
    }
  }

  highlightMatch(text) {
    const searchTerm = this.state.searchItem;
    if (!text || !searchTerm) return text;
    const upperText = text.toUpperCase();
    const upperSearch = searchTerm.toUpperCase();
    const index = upperText.indexOf(upperSearch);
    if (index === -1) return text;
    return (
      <>
        {text.slice(0, index)}
        <span className="font-semibold text-gray-900">
          {text.slice(index, index + searchTerm.length)}
        </span>
        {text.slice(index + searchTerm.length)}
      </>
    );
  }

  renderItem(item) {
    // Handle loading state
    if (item._loading) {
      return (
        <div
          key="loading"
          className="flex items-center justify-center py-3 text-gray-400"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      );
    }

    const { isFilter } = this.props;
    let display = isFilter ? item.shipmentId : item.shipment_id;
    let filterValueExists = false;

    if (isFilter) {
      _.each(item.referenceNumbers, (reference) => {
        if (
          reference.name === 'shipment_id' &&
          reference.optimized &&
          reference.optimized.indexOf(this.state.searchItem) > -1
        ) {
          display = reference.optimized;
          filterValueExists = true;
        }
      });

      if (filterValueExists) {
        return (
          <a
            key={item._id}
            id={item._id}
            className="list-group-item"
            style={{ cursor: 'pointer' }}
          >
            <h4 className="list-group-item-heading">
              <span>{display}</span>
            </h4>
          </a>
        );
      }
      return <div />;
    }

    // Prefer exact match, fall back to first partial match
    const exactMatch = _.find(
      item.reference_numbers,
      (ref) => ref.optimized === this.state.searchItem,
    );
    const match =
      exactMatch ||
      _.find(
        item.reference_numbers,
        (ref) =>
          ref.optimized && ref.optimized.indexOf(this.state.searchItem) > -1,
      );
    if (match) {
      display = match.value;
    }

    return (
      <a
        key={item._id}
        id={item._id}
        className="block py-1 px-2 cursor-pointer hover:bg-black/[0.04] active:bg-black/[0.08] transition-colors group no-underline hover:no-underline"
      >
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-sm">
          <span className="text-gray-900 group-hover:text-black transition-colors truncate">
            {this.highlightMatch(display)}
          </span>
          {display !== item.shipment_id && (
            <span className="text-gray-400 w-24 truncate">
              {item.shipment_id}
            </span>
          )}
          <ShipmentStatusBadge
            status={item.status}
            className="text-[9px] py-0 px-1.5 w-16 justify-center"
          />
        </div>
      </a>
    );
  }

  onSelectionHandler(value, item) {
    const { isFilter } = this.props;
    const onSelection = this.props.onSelect || onSelect;
    let display = isFilter ? item.shipmentId : item.shipment_id;

    if (isFilter) {
      _.each(item.referenceNumbers, (reference) => {
        if (
          reference.name === 'shipment_id' &&
          reference.optimized &&
          reference.optimized.indexOf(this.state.searchItem) > -1
        ) {
          display = reference.optimized;
        }
      });
    } else {
      // Prefer exact match, fall back to first partial match
      const exactMatch = _.find(
        item.reference_numbers,
        (ref) => ref.optimized === this.state.searchItem,
      );
      const match =
        exactMatch ||
        _.find(
          item.reference_numbers,
          (ref) =>
            ref.optimized && ref.optimized.indexOf(this.state.searchItem) > -1,
        );
      if (match) {
        display = match.value;
      }
    }
    onSelection(value, item, this.props.history, display);
  }

  render() {
    const { closeOnSelect, onClose, t, dataTestId, menuStyle } = this.props;
    const { items, isLoading } = this.state;

    const displayItems = isLoading
      ? [{ _loading: true, _id: 'loading' }]
      : items;

    return (
      <SearchBox
        items={displayItems}
        clear={() => this.clear()}
        clearOnSelect={!this.props.isFilter}
        onChange={(event, nGram) => this.onChange(event, nGram)}
        search={(reference) => this.handleSearch(reference)}
        placeholder={
          this.props.isFilter
            ? t('FILTER_BY_SHIPMENT_ID_PLACEHOLDER')
            : t('SEARCH_FOR_REFERENCE')
        }
        onSelect={(value, item) => {
          this.onSelectionHandler(value, item);
          if (closeOnSelect) {
            onClose();
          }
        }}
        renderItem={(item) => this.renderItem(item)}
        nGramLength={5}
        testDataId={dataTestId}
        menuStyle={menuStyle}
        dropdownStyle={{ minWidth: 375 }}
      />
    );
  }
}

ShipmentSearch.propTypes = {
  history: PropTypes.object.isRequired,
  dataTestId: PropTypes.string,
};

export default withRouter(withTranslation()(ShipmentSearch));
